/** REST contract constants shared by client and server. */

export const API_PREFIX = '/api/v1';

export const ROUTES = {
  health: `${API_PREFIX}/health`,
  auth: {
    status: `${API_PREFIX}/auth/status`,
    login: `${API_PREFIX}/auth/login`,
    refresh: `${API_PREFIX}/auth/refresh`,
    me: `${API_PREFIX}/auth/me`,
    changePassword: `${API_PREFIX}/auth/change-password`,
  },
  cockpit: `${API_PREFIX}/cockpit`,
  telemetry: `${API_PREFIX}/telemetry`,
  telemetryHistory: `${API_PREFIX}/telemetry/history`,
  stream: `${API_PREFIX}/stream`,
  snapshot: (id: string) => `${API_PREFIX}/telemetry/${id}`,
  hang: `${API_PREFIX}/hang`,
  makeup: `${API_PREFIX}/makeup`,
  makeupValveMode: (id: string) => `${API_PREFIX}/makeup/valves/${id}/mode`,
  makeupValveCommand: (id: string) => `${API_PREFIX}/makeup/valves/${id}/command`,
  utilities: `${API_PREFIX}/utilities`,
  production: `${API_PREFIX}/production`,
  productionBatches: `${API_PREFIX}/production/batches`,
  productionBatch: (id: string) => `${API_PREFIX}/production/batches/${id}`,
  productionBatchEvents: (id: string) => `${API_PREFIX}/production/batches/${id}/events`,
  cost: {
    prices: `${API_PREFIX}/cost/prices`,
    price: (id: string) => `${API_PREFIX}/cost/prices/${id}`,
    monthly: `${API_PREFIX}/cost/monthly`,
    monthlyEntry: (month: string) => `${API_PREFIX}/cost/monthly/${month}`,
  },
  ai: `${API_PREFIX}/ai`,
  aiBacktest: `${API_PREFIX}/ai/backtest`,
  aiModels: `${API_PREFIX}/ai/models`,
  aiFeedback: `${API_PREFIX}/ai/feedback`,
  dataHealth: `${API_PREFIX}/data-health`,
  alarms: {
    list: `${API_PREFIX}/alarms`,
    history: `${API_PREFIX}/alarms/history`,
    ack: (id: string) => `${API_PREFIX}/alarms/${id}/ack`,
    shelve: (id: string) => `${API_PREFIX}/alarms/${id}/shelve`,
    unshelve: (id: string) => `${API_PREFIX}/alarms/${id}/unshelve`,
    toWorkOrder: (id: string) => `${API_PREFIX}/alarms/${id}/work-order`,
    notifications: `${API_PREFIX}/alarms/notifications`,
    exportCsv: `${API_PREFIX}/alarms/export.csv`,
    suppressions: `${API_PREFIX}/alarms/suppressions`,
    suppression: (id: string) => `${API_PREFIX}/alarms/suppressions/${id}`,
  },
  equipment: {
    list: `${API_PREFIX}/equipment`,
    detail: (slug: string) => `${API_PREFIX}/equipment/${slug}`,
  },
  lab: {
    list: `${API_PREFIX}/lab/entries`,
    submit: `${API_PREFIX}/lab/submit`,
    chain: (id: string) => `${API_PREFIX}/lab/entries/${id}/chain`,
    correct: (id: string) => `${API_PREFIX}/lab/entries/${id}/correct`,
    calibrate: `${API_PREFIX}/lab/calibration-proposals`,
    qc: `${API_PREFIX}/lab/qc`,
  },
  recipes: {
    list: `${API_PREFIX}/recipes`,
    detail: (id: string) => `${API_PREFIX}/recipes/${id}`,
    transition: (id: string) => `${API_PREFIX}/recipes/${id}/transition`,
    transitions: (id: string) => `${API_PREFIX}/recipes/${id}/transitions`,
  },
  esign: {
    create: `${API_PREFIX}/esign`,
    list: `${API_PREFIX}/esign`,
    detail: (id: string) => `${API_PREFIX}/esign/${id}`,
    verify: (id: string) => `${API_PREFIX}/esign/${id}/verify`,
    publicKey: `${API_PREFIX}/esign/public-key`,
  },
  exports: {
    list: `${API_PREFIX}/exports`,
    create: `${API_PREFIX}/exports`,
    detail: (id: string) => `${API_PREFIX}/exports/${id}`,
    link: (id: string) => `${API_PREFIX}/exports/${id}/link`,
    download: (id: string) => `${API_PREFIX}/exports/${id}/download`,
    access: (id: string) => `${API_PREFIX}/exports/${id}/access`,
  },
  mobile: `${API_PREFIX}/mobile/preview`,
  workOrders: `${API_PREFIX}/work-orders`,
  workOrder: (id: string) => `${API_PREFIX}/work-orders/${id}`,
  workOrderStatus: (id: string) => `${API_PREFIX}/work-orders/${id}/status`,
  workOrderEvents: (id: string) => `${API_PREFIX}/work-orders/${id}/events`,
  audit: `${API_PREFIX}/audit`,
  auditVerify: `${API_PREFIX}/audit/verify`,
  openapi: `${API_PREFIX}/openapi.json`,
  admin: {
    users: `${API_PREFIX}/admin/users`,
    user: (id: string) => `${API_PREFIX}/admin/users/${id}`,
    userPassword: (id: string) => `${API_PREFIX}/admin/users/${id}/password`,
    schema: `${API_PREFIX}/admin/schema`,
    retention: `${API_PREFIX}/admin/retention`,
  },
  notify: {
    receipt: `${API_PREFIX}/notify/receipt`,
    config: `${API_PREFIX}/notify/config`,
  },
} as const;

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export const ErrorCode = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION: 'VALIDATION',
  CONFLICT: 'CONFLICT', // e.g. ETag precondition failed
  LOCKED: 'LOCKED', // e.g. snapshot sync in progress
} as const;
